import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import PageWrap from '../../../components/auth/page-wrap';
import { getErrorMessage, getErrorStatus, getFieldErrors, isApiValidationError } from '../../../core/api/response-handler';
import { verificaContLaInscriere } from '../api/otp.api';
import { InapoiLaAlegere } from '../components/AlegereIntrare';
import { esteEmailValid, stareVerificareCod } from '../utils/otp-cod';
import { eroareInscriere, esteLimitaDeCoduri, MESAJ_CONT_ELEV, pasDupaInscriere } from '../utils/inscriere-email';
import { mesajLimitaCod } from '../utils/otp-mesaje';
import { AUTH_PAROLA_ACTIVA } from '../utils/parola-flag';
import { trackSignupEmailCheckFailed, trackSignupEmailChecked } from '@/telemetry/posthog-events';

/**
 * Ecranul «Înscriere» (item 1, cererile ECOTIC din 21.09): omul scrie emailul, iar ecranul afla
 * daca adresa are deja cont — `POST /api/auth/otp/inscriere`, prin `verificaContLaInscriere`.
 *
 * Trei iesiri, decise de `pasDupaInscriere` (pur, testat):
 * - cont non-ELEV → codul de intrare a plecat deja → `/cod-de-acces/verificare`, cu acelasi
 *   `state` ca fluxul de logare (`stareVerificareCod`);
 * - cont de ELEV → nu pleaca niciun cod (emailul unui elev nu identifica un cont unic peste
 *   editii) → inapoi la autentificare, cu emailul si un mesaj scurt;
 * - niciun cont → panoul „Nu există cont…", cu «Înregistrează școala nouă» (emailul merge
 *   precompletat pe `/profesor/inregistrare`) si «Încearcă altă adresă».
 *
 * Si doua iesiri pe ramura de EROARE, din 2026-10-08, cand contul exista dar codul n-a plecat:
 * - 429 `LIMITA_COD` → tot `/cod-de-acces/verificare`, cu un mesaj care spune ca NU s-a trimis un
 *   cod nou si cand se poate cere altul: codurile deja primite raman valabile, deci omul are ce
 *   scrie. A-l opri aici cu o eroare l-ar lasa cu un cod bun si fara unde sa-l puna;
 * - 503 `TRIMITERE_ESUATA` → ramane pe loc, cu „nu am putut trimite codul" (`eroareInscriere`).
 * In niciunul dintre cazuri ecranul nu afirma ca a plecat un cod.
 *
 * 🔴 Pana pe 21.09 ecranul chema `/otp/request`, cu raspuns UNIFORM, si nu avea voie sa spuna daca
 * exista cont. Ruta noua spune, DELIBERAT (decizia owner-ului) — motivul si limitele sunt in
 * `backend/docs/knowledge/otp-inscriere-oracol-deliberat-2026-09-21.md`. Deci textul „Nu există
 * cont cu adresa…" nu e o scapare de enumerare; e cerinta.
 *
 * Ruta `/cod-de-acces` e pastrata: o folosesc si «Cere un cod» si «Schimbă» din
 * `VerificareCodPage`. Pentru un profesor cu cont, ruta noua trimite codul exact pe calea lui
 * `/otp/request` (aceeasi limita, acelasi hash), deci acele doua linkuri merg mai departe la fel.
 *
 * ⚠️ `type="text"` + `inputMode="email"`, nu `type="email"`: validarea nativa a browserului ar
 * bloca `submit`-ul INAINTE de `trimite()`, deci mesajul propriu n-ar mai apuca sa se randeze —
 * masurat cu Puppeteer pe versiunea anterioara (eroareTelefon: null).
 *
 * ⚠️ Ruta de backend exista doar cu `auth.otp.enabled=true`; altfel 404 — tratat ca mesaj generic
 * (`eroareInscriere`), nu ca eroare de pagina.
 */

export default function CerereCodPage() {
    const { PATHS, PAGES } = useRoutesProvider();
    const navigate = useNavigate();
    const location = useLocation();

    // Emailul scris pe ecranul de autentificare, daca omul vine de acolo (sau de pe «Schimbă»
    // din verificare): nu-l punem sa-l scrie a doua oara.
    const initial = (location.state as { identificator?: string } | null)?.identificator ?? '';

    const [email, setEmail] = React.useState(initial);
    const [seTrimite, setSeTrimite] = React.useState(false);
    const [eroareCamp, setEroareCamp] = React.useState<string | null>(null);
    const [eroare, setEroare] = React.useState<string | null>(null);
    /** Adresa pentru care serverul a spus „nu exista cont"; `null` = se vede formularul. */
    const [faraCont, setFaraCont] = React.useState<string | null>(null);
    const campRef = React.useRef<HTMLInputElement>(null);
    const titluPanouRef = React.useRef<HTMLHeadingElement>(null);
    /* Focusul se muta DUPA randare: campul si panoul nu exista in acelasi timp in DOM, deci
       un `focus()` chemat in handler ar tinti un element care inca nu s-a montat. */
    const focusPeCamp = React.useRef(false);

    React.useEffect(() => {
        if (faraCont !== null) {
            // Panoul inlocuieste formularul: fara focus mutat, un cititor de ecran ramane pe un
            // buton care nu mai exista si nu afla ce s-a intamplat.
            titluPanouRef.current?.focus();
        } else if (focusPeCamp.current) {
            focusPeCamp.current = false;
            campRef.current?.focus();
        }
    }, [faraCont]);

    const potTrimite = esteEmailValid(email) && !seTrimite;

    async function trimite(e: React.FormEvent) {
        e.preventDefault();
        if (seTrimite) return;
        setEroare(null);
        if (!esteEmailValid(email)) {
            // Butonul e `aria-disabled`, nu `disabled` (motivul e mai jos, la Buton) — deci
            // click-ul chiar ajunge aici. Fara mesaj si focus, omul nu afla de ce nu s-a
            // intamplat nimic (constatarea #19, S1).
            setEroareCamp('Introdu o adresă de email validă.');
            campRef.current?.focus();
            return;
        }
        const adresa = email.trim();
        setSeTrimite(true);
        setEroareCamp(null);
        try {
            const raspuns = await verificaContLaInscriere(adresa);
            const pas = pasDupaInscriere(raspuns);
            trackSignupEmailChecked(pas);
            switch (pas) {
                case 'verificare':
                    // Raspunsul nu aduce mesaj (spre deosebire de `/otp/request`), deci textul de
                    // pe ecranul urmator e al nostru — si e adevarat, pentru ca `codTrimis` o spune.
                    navigate(PATHS[PAGES.VERIFICARE_COD], {
                        state: stareVerificareCod(adresa, 'Ți-am trimis pe email un cod din șase cifre.', Date.now())
                    });
                    return;
                case 'autentificare-elev':
                    navigate(PATHS[PAGES.LOGIN_EMAIL], { state: { email: adresa, mesaj: MESAJ_CONT_ELEV } });
                    return;
                case 'fara-cont':
                    setFaraCont(adresa);
                    return;
            }
        } catch (err) {
            const status = getErrorStatus(err);
            const motiv = isApiValidationError(err) ? err.motiv : undefined;
            trackSignupEmailCheckFailed(status);
            if (esteLimitaDeCoduri(status, motiv)) {
                // Niciun cod nou, dar cele primite merg: ecranul de cod, cu mesajul care o spune
                // si cu «Trimite alt cod» tinut inactiv pana la clipa data de server.
                const secunde = isApiValidationError(err) ? err.secundePanaLaCodNou : undefined;
                const acum = Date.now();
                navigate(PATHS[PAGES.VERIFICARE_COD], {
                    state: stareVerificareCod(
                        adresa,
                        mesajLimitaCod(secunde),
                        acum,
                        undefined,
                        secunde === undefined ? undefined : acum + secunde * 1000
                    )
                });
                return;
            }
            const { camp, general } = eroareInscriere(
                status,
                getFieldErrors(err)?.email,
                getErrorMessage(err, ''),
                motiv
            );
            setEroareCamp(camp);
            setEroare(general);
            if (camp) campRef.current?.focus();
        } finally {
            setSeTrimite(false);
        }
    }

    function altaAdresa() {
        focusPeCamp.current = true;
        setEmail('');
        setEroareCamp(null);
        setEroare(null);
        setFaraCont(null);
    }

    return (
        <PageWrap>
            <h1 className="sr-only">Înscriere</h1>
            <Card className="mx-auto mb-4 w-full max-w-md">
                <CardHeader>
                    <InapoiLaAlegere />
                    {faraCont === null && (
                        <CardDescription className="mt-2">
                            Scrie adresa de email. Dacă ai deja cont, îți trimitem un cod de intrare; dacă nu, te ajutăm să-ți înscrii școala.
                        </CardDescription>
                    )}
                </CardHeader>
                <CardContent>
                    {faraCont !== null ? (
                        <section aria-labelledby="inscriere-fara-cont-titlu" className="space-y-4">
                            {/* `tabIndex={-1}`: primeste focusul programatic cand apare panoul,
                                fara sa intre in ordinea de tab. `break-all` pe adresa: la 360px
                                o adresa lunga fara spatii ar iesi din card. */}
                            <h2
                                id="inscriere-fara-cont-titlu"
                                ref={titluPanouRef}
                                tabIndex={-1}
                                className="m-0 text-fluid-base font-semibold text-dark outline-none"
                            >
                                Nu există cont cu adresa <span className="break-all">{faraCont}</span>
                            </h2>
                            <p className="m-0 text-sm text-gray-600">
                                Dacă ești profesor și vrei să-ți înscrii școala în program, continuă cu înregistrarea. Dacă ai greșit adresa, încearcă alta.
                            </p>
                            <Button
                                type="button"
                                size="lg"
                                className="w-full touch-target"
                                onClick={() => navigate(PATHS[PAGES.TEACHER_REGISTER], { state: { email: faraCont } })}
                            >
                                Înregistrează școala nouă
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="lg"
                                className="w-full touch-target"
                                onClick={altaAdresa}
                            >
                                Încearcă altă adresă
                            </Button>
                        </section>
                    ) : (
                        <form onSubmit={trimite} className="space-y-4" noValidate>
                            <div className="space-y-2">
                                <label htmlFor="otp-identificator" className="form-label-bold">
                                    Email
                                </label>
                                <Input
                                    id="otp-identificator"
                                    ref={campRef}
                                    type="text"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (eroareCamp) setEroareCamp(null);
                                        if (eroare) setEroare(null);
                                    }}
                                    placeholder="exemplu@email.ro"
                                    className="form-input-styled"
                                    aria-required="true"
                                    aria-invalid={!!eroareCamp}
                                    aria-describedby={eroareCamp ? 'otp-identificator-eroare' : undefined}
                                />
                                {/* Eroarea de camp sta SUB camp, legata prin `aria-describedby`:
                                    la 360px o alerta departe de camp nu se citeste ca fiind a lui. */}
                                {eroareCamp && (
                                    <p id="otp-identificator-eroare" role="alert" className="m-0 text-sm text-error">
                                        {eroareCamp}
                                    </p>
                                )}
                            </div>

                            {eroare && (
                                <p role="alert" data-eroare-otp className="rounded-lg border border-error bg-error/10 p-3 text-small text-error m-0">
                                    {eroare}
                                </p>
                            )}

                            {/* `aria-disabled`, nu `disabled`: `buttonVariants` are
                                `disabled:opacity-50`, iar o eticheta stearsa la 50% cade sub
                                pragul de contrast. Dar starea TREBUIE si sa se vada — un buton
                                care arata activ si nu raspunde e mai rau decat unul stins. */}
                            <Button
                                type="submit"
                                size="lg"
                                aria-disabled={!potTrimite}
                                aria-busy={seTrimite}
                                className={'min-h-11 w-full ' + (potTrimite ? '' : 'cursor-not-allowed bg-light text-dark hover:bg-light')}
                            >
                                {seTrimite ? 'Se verifică…' : 'Continuă'}
                            </Button>
                        </form>
                    )}

                    {/* Iesirile catre parola dispar odata cu ea (`VITE_AUTH_PAROLA` oprit):
                        rutele nu se mai inregistreaza, deci linkul ar cadea pe catch-all.
                        ⚠️ `py-3.5` e aria de atingere (44px), nu decor — vezi nota din
                        `VerificareCodPage`; `mt-6` lasa loc intre ea si butonul de deasupra.
                        Linkul „Înapoi la logare" de aici a disparut: «← Înapoi», sus
                        (`InapoiLaAlegere`), duce la alegerea de pe `/autentificare`. */}
                    {AUTH_PAROLA_ACTIVA && (
                        <p className="mt-6 text-center text-sm text-gray-600 m-0">
                            Nu primești codul?{' '}
                            <Link to={PATHS[PAGES.FORGOTTEN_PASSWORD]} className="py-3.5 text-magenta underline">
                                Poți intra resetând parola prin email
                            </Link>
                        </p>
                    )}
                </CardContent>
            </Card>
        </PageWrap>
    );
}
